import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import {format} from 'date-fns';
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Urgency = {
    Low: "L",
    Medium: "M",
    High: "H"
};
export type UrgencyType = typeof Urgency[keyof typeof Urgency];
export const Impact = {
    Low: "L",
    Medium: "M",
    High: "H"
};
export type ImpactType = typeof Impact[keyof typeof Impact];
export const AssignedToType = {
    User: "U",
    Group: "G"
};
export type AssignedToTypeType = typeof AssignedToType[keyof typeof AssignedToType];
export type TaskWaitingOn = {waitType: 'C'|'T', waitOn: uuid, waitUntil: number, description: string};
export type TaskTieIns = {module: string, entity: string, id: string};
export type TaskNotification = {status: 'r'|'c'|'w', from: uuid, to: uuid, message: string};
export type TaskBaseVersion = {addedOn: number | null, dbAddedOn: number | null};
class Task extends Base implements EntityType {
    taskId: uuid;
    title: string;
    dueDate: Date;
    duration?: number | null;
    urgency: UrgencyType;
    impact: ImpactType;
    startTime?: Date | null;
    elapsedTime: number;
    lastStart: number;
    running: boolean;
    timerStarted: boolean;
    private: boolean;
    location?: string | null;
    docketId?: uuid | null;
    roomNumber?: string | null;
    categories: uuid[];
    waitingOn: TaskWaitingOn[];
    completionDate?: Date | null;
    assignedToType: AssignedToTypeType;
    assignedTo?: uuid | null;
    relatedTo: uuid[];
    tags: string[];
    tieIns: TaskTieIns[];
    associatedRate: number;
    activityTypeIds: uuid[];
    billable: boolean;
    laborRate?: number | null;
    laborRateOverride?: number | null;
    isLaborRateOverridden: boolean;
    travelRate?: number | null;
    travelRateOverride?: number | null;
    isTravelRateOverridden: boolean;
    laborTime: number;
    laborTimeOverride: number;
    isLaborTimeOverridden: boolean;
    travelTime: number;
    travelTimeOverride: number;
    isTravelTimeOverridden: boolean;
    cost: number;
    costOverride: number;
    isCostOverridden: boolean;
    noCharge: boolean;
    hideFromInvoice: boolean;
    flatFeeBucket?: uuid | null;
    flatFeeMilestoneBucket?: uuid | null;
    amountPaid: number;
    invoiceId?: uuid | null;
    invoiceNotes?: string | null;
    notification?: TaskNotification | null;
    fromTemplateId?: uuid | null;
    emojis: string[];
    seriesId?: uuid | null;
    recurrenceId?: Date | null;
    sources: string[];
    targets: string[];
    baseVersion?: TaskBaseVersion | null;
    pendingOp?: string | null;
    constructor(doc: typeof Task | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            taskId: genUUID(),
            title: null,
            dueDate: null,
            urgency: null,
            impact: null,
            timerStarted: false,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "task";
        // @ts-ignore
        this.taskId = doc._taskId || doc.taskId || doc.taskid || genUUID();
        // @ts-ignore
        this.title = doc._title || doc.title || null;
        // @ts-ignore
        this.dueDate = DateUtil.timestampOnly(doc._dueDate) || DateUtil.timestampOnly(doc.dueDate) || DateUtil.timestampOnly(doc.duedate) || null;
        // @ts-ignore
        this.duration = util.coalesce(util.parseFloat(doc._duration), util.parseFloat(doc.duration), util.parseFloat(doc.duration), null);
        // @ts-ignore
        this.urgency = util.trimRight(doc._urgency) || util.trimRight(doc.urgency) || null;
        // @ts-ignore
        this.impact = util.trimRight(doc._impact) || util.trimRight(doc.impact) || null;
        // @ts-ignore
        this.startTime = DateUtil.timestampOnly(doc._startTime) || DateUtil.timestampOnly(doc.startTime) || DateUtil.timestampOnly(doc.starttime) || null;
        // @ts-ignore
        this.elapsedTime = util.coalesce(util.parseInt(doc._elapsedTime), util.parseInt(doc.elapsedTime), util.parseInt(doc.elapsedtime), 0);
        // @ts-ignore
        this.lastStart = util.coalesce(util.parseFloat(doc._lastStart), util.parseFloat(doc.lastStart), util.parseFloat(doc.laststart), 0.0);
        // @ts-ignore
        this.running = util.defaultBoolean([doc.running, doc._running, doc.running, false]);
        // @ts-ignore
        this.timerStarted = util.defaultBoolean([doc.timerStarted, doc._timerStarted, doc.timerstarted, false]);
        // @ts-ignore
        this.private = util.defaultBoolean([doc.private, doc._private, doc.private, false]);
        // @ts-ignore
        this.location = doc._location || doc.location || null;
        // @ts-ignore
        this.docketId = doc._docketId || doc.docketId || doc.docketid || null;
        // @ts-ignore
        this.roomNumber = doc._roomNumber || doc.roomNumber || doc.roomnumber || null;
        // @ts-ignore
        this.categories = doc._categories || doc.categories || [];
        // @ts-ignore
        this.waitingOn = doc._waitingOn || doc.waitingOn || doc.waitingon || [];
        // @ts-ignore
        this.completionDate = DateUtil.timestampOnly(doc._completionDate) || DateUtil.timestampOnly(doc.completionDate) || DateUtil.timestampOnly(doc.completiondate) || null;
        // @ts-ignore
        this.assignedToType = util.trimRight(doc._assignedToType) || util.trimRight(doc.assignedToType) || util.trimRight(doc.assignedtotype) || "U";
        // @ts-ignore
        this.assignedTo = doc._assignedTo || doc.assignedTo || doc.assignedto || null;
        // @ts-ignore
        this.relatedTo = doc._relatedTo || doc.relatedTo || doc.relatedto || [];
        // @ts-ignore
        this.tags = doc._tags || doc.tags || [];
        // @ts-ignore
        this.tieIns = doc._tieIns || doc.tieIns || doc.tieins || [];
        // @ts-ignore
        this.associatedRate = util.coalesce(util.parseFloat(doc._associatedRate), util.parseFloat(doc.associatedRate), util.parseFloat(doc.associatedrate), 0.0);
        // @ts-ignore
        this.activityTypeIds = doc._activityTypeIds || doc.activityTypeIds || doc.activitytypeids || [];
        // @ts-ignore
        this.billable = util.defaultBoolean([doc.billable, doc._billable, doc.billable, false]);
        // @ts-ignore
        this.laborRate = util.coalesce(util.parseFloat(doc._laborRate), util.parseFloat(doc.laborRate), util.parseFloat(doc.laborrate), null);
        // @ts-ignore
        this.laborRateOverride = util.coalesce(util.parseFloat(doc._laborRateOverride), util.parseFloat(doc.laborRateOverride), util.parseFloat(doc.laborrateoverride), null);
        // @ts-ignore
        this.isLaborRateOverridden = util.defaultBoolean([doc.isLaborRateOverridden, doc._isLaborRateOverridden, doc.islaborrateoverridden, false]);
        // @ts-ignore
        this.travelRate = util.coalesce(util.parseFloat(doc._travelRate), util.parseFloat(doc.travelRate), util.parseFloat(doc.travelrate), null);
        // @ts-ignore
        this.travelRateOverride = util.coalesce(util.parseFloat(doc._travelRateOverride), util.parseFloat(doc.travelRateOverride), util.parseFloat(doc.travelrateoverride), null);
        // @ts-ignore
        this.isTravelRateOverridden = util.defaultBoolean([doc.isTravelRateOverridden, doc._isTravelRateOverridden, doc.istravelrateoverridden, false]);
        // @ts-ignore
        this.laborTime = util.coalesce(util.parseFloat(doc._laborTime), util.parseFloat(doc.laborTime), util.parseFloat(doc.labortime), 0);
        // @ts-ignore
        this.laborTimeOverride = util.coalesce(util.parseFloat(doc._laborTimeOverride), util.parseFloat(doc.laborTimeOverride), util.parseFloat(doc.labortimeoverride), 0);
        // @ts-ignore
        this.isLaborTimeOverridden = util.defaultBoolean([doc.isLaborTimeOverridden, doc._isLaborTimeOverridden, doc.islabortimeoverridden, false]);
        // @ts-ignore
        this.travelTime = util.coalesce(util.parseFloat(doc._travelTime), util.parseFloat(doc.travelTime), util.parseFloat(doc.traveltime), 0);
        // @ts-ignore
        this.travelTimeOverride = util.coalesce(util.parseFloat(doc._travelTimeOverride), util.parseFloat(doc.travelTimeOverride), util.parseFloat(doc.traveltimeoverride), 0);
        // @ts-ignore
        this.isTravelTimeOverridden = util.defaultBoolean([doc.isTravelTimeOverridden, doc._isTravelTimeOverridden, doc.istraveltimeoverridden, false]);
        // @ts-ignore
        this.cost = util.coalesce(util.parseFloat(doc._cost), util.parseFloat(doc.cost), util.parseFloat(doc.cost), 0);
        // @ts-ignore
        this.costOverride = util.coalesce(util.parseFloat(doc._costOverride), util.parseFloat(doc.costOverride), util.parseFloat(doc.costoverride), 0);
        // @ts-ignore
        this.isCostOverridden = util.defaultBoolean([doc.isCostOverridden, doc._isCostOverridden, doc.iscostoverridden, false]);
        // @ts-ignore
        this.noCharge = util.defaultBoolean([doc.noCharge, doc._noCharge, doc.nocharge, false]);
        // @ts-ignore
        this.hideFromInvoice = util.defaultBoolean([doc.hideFromInvoice, doc._hideFromInvoice, doc.hidefrominvoice, false]);
        // @ts-ignore
        this.flatFeeBucket = doc._flatFeeBucket || doc.flatFeeBucket || doc.flatfeebucket || null;
        // @ts-ignore
        this.flatFeeMilestoneBucket = doc._flatFeeMilestoneBucket || doc.flatFeeMilestoneBucket || doc.flatfeemilestonebucket || null;
        // @ts-ignore
        this.amountPaid = util.coalesce(util.parseFloat(doc._amountPaid), util.parseFloat(doc.amountPaid), util.parseFloat(doc.amountpaid), 0.0);
        // @ts-ignore
        this.invoiceId = doc._invoiceId || doc.invoiceId || doc.invoiceid || null;
        // @ts-ignore
        this.invoiceNotes = doc._invoiceNotes || doc.invoiceNotes || doc.invoicenotes || null;
        // @ts-ignore
        this.notification = doc._notification || doc.notification || {};
        // @ts-ignore
        this.fromTemplateId = doc._fromTemplateId || doc.fromTemplateId || doc.fromtemplateid || null;
        // @ts-ignore
        this.emojis = doc._emojis || doc.emojis || [];
        // @ts-ignore
        this.seriesId = doc._seriesId || doc.seriesId || doc.seriesid || null;
        // @ts-ignore
        this.recurrenceId = DateUtil.timestampOnly(doc._recurrenceId) || DateUtil.timestampOnly(doc.recurrenceId) || DateUtil.timestampOnly(doc.recurrenceid) || null;
        // @ts-ignore
        this.sources = doc._sources || doc.sources || [];
        // @ts-ignore
        this.targets = doc._targets || doc.targets || [];
        // @ts-ignore
        this.baseVersion = doc._baseVersion || doc.baseVersion || doc.baseversion || {};
        // @ts-ignore
        this.pendingOp = doc._pendingOp || doc.pendingOp || doc.pendingop || null;
    }
    get id(): uuid { return this.taskId; }
    static URGENCY_VALUES = ["L", "M", "H"];
    static IMPACT_VALUES = ["L", "M", "H"];
    addCategory(category: uuid): uuid {
        if (!category) throw new ValidationError('No nulls allowed in an array!');
        if (!this.categories) this.categories = [];
        this.categories.push(category);
        return category;
    }
    updateCategory(category: uuid) {
        if (!this.categories) this.categories = [];
        const index = this.categories.findIndex(item => item === category);
        if (index !== -1) { this.categories[index] = category; }
        else this.addCategory(category);
    }
    removeCategory(category: uuid) {
        if (!this.categories) return;
        let foundItems = this.categories?.filter(item => item === category);
        if (foundItems && foundItems.length > 0) {
            this.categories = this.categories.filter(item => !(item === category));
        }
    }
    abandonCategories() { this.categories = []; }
    addWaitingOn(waitingOn: TaskWaitingOn): TaskWaitingOn {
        if (!waitingOn) throw new ValidationError('No nulls allowed in an array!');
        if (!this.waitingOn) this.waitingOn = [];
        this.waitingOn.push(waitingOn);
        return waitingOn;
    }
    updateWaitingOn(waitingOn: TaskWaitingOn) {
        if (!this.waitingOn) this.waitingOn = [];
        const index = this.waitingOn.findIndex(item => item === waitingOn);
        if (index !== -1) { this.waitingOn[index] = waitingOn; }
        else this.addWaitingOn(waitingOn);
    }
    removeWaitingOn(waitingOn: TaskWaitingOn) {
        if (!this.waitingOn) return;
        let foundItems = this.waitingOn?.filter(item => item === waitingOn);
        if (foundItems && foundItems.length > 0) {
            this.waitingOn = this.waitingOn.filter(item => !(item === waitingOn));
        }
    }
    abandonWaitingOn() { this.waitingOn = []; }
    static ASSIGNED_TO_TYPE_VALUES = ["U", "G"];
    addRelatedTo(relatedTo: uuid): uuid {
        if (!relatedTo) throw new ValidationError('No nulls allowed in an array!');
        if (!this.relatedTo) this.relatedTo = [];
        this.relatedTo.push(relatedTo);
        return relatedTo;
    }
    updateRelatedTo(relatedTo: uuid) {
        if (!this.relatedTo) this.relatedTo = [];
        const index = this.relatedTo.findIndex(item => item === relatedTo);
        if (index !== -1) { this.relatedTo[index] = relatedTo; }
        else this.addRelatedTo(relatedTo);
    }
    removeRelatedTo(relatedTo: uuid) {
        if (!this.relatedTo) return;
        let foundItems = this.relatedTo?.filter(item => item === relatedTo);
        if (foundItems && foundItems.length > 0) {
            this.relatedTo = this.relatedTo.filter(item => !(item === relatedTo));
        }
    }
    abandonRelatedTo() { this.relatedTo = []; }
    addTag(tag: string): string {
        if (!tag) throw new ValidationError('No nulls allowed in an array!');
        if (!this.tags) this.tags = [];
        this.tags.push(tag);
        return tag;
    }
    updateTag(tag: string) {
        if (!this.tags) this.tags = [];
        const index = this.tags.findIndex(item => item === tag);
        if (index !== -1) { this.tags[index] = tag; }
        else this.addTag(tag);
    }
    removeTag(tag: string) {
        if (!this.tags) return;
        let foundItems = this.tags?.filter(item => item === tag);
        if (foundItems && foundItems.length > 0) {
            this.tags = this.tags.filter(item => !(item === tag));
        }
    }
    abandonTags() { this.tags = []; }
    addTieIn(tieIn: TaskTieIns): TaskTieIns {
        if (!tieIn) throw new ValidationError('No nulls allowed in an array!');
        if (!this.tieIns) this.tieIns = [];
        this.tieIns.push(tieIn);
        return tieIn;
    }
    updateTieIn(tieIn: TaskTieIns) {
        if (!this.tieIns) this.tieIns = [];
        const index = this.tieIns.findIndex(item => item === tieIn);
        if (index !== -1) { this.tieIns[index] = tieIn; }
        else this.addTieIn(tieIn);
    }
    removeTieIn(tieIn: TaskTieIns) {
        if (!this.tieIns) return;
        let foundItems = this.tieIns?.filter(item => item === tieIn);
        if (foundItems && foundItems.length > 0) {
            this.tieIns = this.tieIns.filter(item => !(item === tieIn));
        }
    }
    abandonTieIns() { this.tieIns = []; }
    addActivityTypeId(activityTypeId: uuid): uuid {
        if (!activityTypeId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.activityTypeIds) this.activityTypeIds = [];
        this.activityTypeIds.push(activityTypeId);
        return activityTypeId;
    }
    updateActivityTypeId(activityTypeId: uuid) {
        if (!this.activityTypeIds) this.activityTypeIds = [];
        const index = this.activityTypeIds.findIndex(item => item === activityTypeId);
        if (index !== -1) { this.activityTypeIds[index] = activityTypeId; }
        else this.addActivityTypeId(activityTypeId);
    }
    removeActivityTypeId(activityTypeId: uuid) {
        if (!this.activityTypeIds) return;
        let foundItems = this.activityTypeIds?.filter(item => item === activityTypeId);
        if (foundItems && foundItems.length > 0) {
            this.activityTypeIds = this.activityTypeIds.filter(item => !(item === activityTypeId));
        }
    }
    abandonActivityTypeIds() { this.activityTypeIds = []; }
    addEmoji(emoji: string): string {
        if (!emoji) throw new ValidationError('No nulls allowed in an array!');
        if (!this.emojis) this.emojis = [];
        this.emojis.push(emoji);
        return emoji;
    }
    updateEmoji(emoji: string) {
        if (!this.emojis) this.emojis = [];
        const index = this.emojis.findIndex(item => item === emoji);
        if (index !== -1) { this.emojis[index] = emoji; }
        else this.addEmoji(emoji);
    }
    removeEmoji(emoji: string) {
        if (!this.emojis) return;
        let foundItems = this.emojis?.filter(item => item === emoji);
        if (foundItems && foundItems.length > 0) {
            this.emojis = this.emojis.filter(item => !(item === emoji));
        }
    }
    abandonEmojis() { this.emojis = []; }
    addSource(source: string): string {
        if (!source) throw new ValidationError('No nulls allowed in an array!');
        if (!this.sources) this.sources = [];
        this.sources.push(source);
        return source;
    }
    updateSource(source: string) {
        if (!this.sources) this.sources = [];
        const index = this.sources.findIndex(item => item === source);
        if (index !== -1) { this.sources[index] = source; }
        else this.addSource(source);
    }
    removeSource(source: string) {
        if (!this.sources) return;
        let foundItems = this.sources?.filter(item => item === source);
        if (foundItems && foundItems.length > 0) {
            this.sources = this.sources.filter(item => !(item === source));
        }
    }
    abandonSources() { this.sources = []; }
    addTarget(target: string): string {
        if (!target) throw new ValidationError('No nulls allowed in an array!');
        if (!this.targets) this.targets = [];
        this.targets.push(target);
        return target;
    }
    updateTarget(target: string) {
        if (!this.targets) this.targets = [];
        const index = this.targets.findIndex(item => item === target);
        if (index !== -1) { this.targets[index] = target; }
        else this.addTarget(target);
    }
    removeTarget(target: string) {
        if (!this.targets) return;
        let foundItems = this.targets?.filter(item => item === target);
        if (foundItems && foundItems.length > 0) {
            this.targets = this.targets.filter(item => !(item === target));
        }
    }
    abandonTargets() { this.targets = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for task"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.taskId; }
    displayFn() { return this.title; }
    static override getNonRefArrayFields(): string[] { return ["categories", "waitingOn", "relatedTo", "tags", "tieIns", "activityTypeIds", "emojis", "sources", "targets"]; }
    static override getDefaultEntries(): Task[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'task',
    dataArea: 'Tasks and Time',
    table: 'TASK',
    // OWA-1650: a Task on an invoice is billed work, so deleting it is refused until it is taken off the invoice.
    deleteBlockedWhen: {nonEmpty: 'invoiceId'},
    comment: 'A work item assigned to a user or group, with due date, urgency, impact, and a built-in timer for tracking actual time spent. Tasks can be linked to cases (matters), sales leads, or marketing campaigns and carry a full set of billable time-entry fields — labor/travel rates, time overrides, flat-fee bucket, and invoice association — making them the primary record of billable work done on behalf of a client.',
    fields: {
        taskId: {type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for task'},
        title: {
            type: 'string', required: true, comment: 'A one-line description of the task, starting with a verb'
        },
        dueDate: {
            type: 'timestamp', required: true, comment: 'The date and optional time to finish this task',
        },
        duration: {
            type: 'float', comment: 'How long will it take to finish this task (in hours)?',
        },
        urgency: {
            type: 'char',
            maxLength: 1,
            required: true,
            values: ['L', 'M', 'H'],
            valueNames: ['Low', 'Medium', 'High'],
            comment: 'How urgent is this task?',
        },
        impact: {
            type: 'char',
            maxLength: 1,
            required: true,
            values: ['L', 'M', 'H'],
            valueNames: ['Low', 'Medium', 'High'],
            comment: 'What is the impact of successfully completing this task?',
        },

        // Duration format, e.g. "02:00" for 2am
        startTime: {
            type: 'timestamp', comment: 'Optional start time'
        },

        // Timer fields
        elapsedTime: {
            type: 'bigint', default: 0, comment: 'Amount of time actually spent working on the task (in milliseconds)'
        },
        lastStart: {
            type: 'float', default: 0.0, comment: 'When the timer was last started (epoch milliseconds); 0 when not running'
        },
        running: {
            type: 'boolean', default: false, comment: 'Is this task being actively timed?'
        },
        timerStarted: {
            type: 'boolean', required: true, default: false, comment: 'Has the task timer ever been started? Permanently locks the assignee when true'
        },

        private: {
            type: 'boolean', comment: 'Hide this task from others?', default: false,
            keptWhenOmitted: true,
        },

        location: {
            type: 'string', comment: 'A optional location where this task must be done',
        },

        docketId: {
            type: 'uuid', entityType: 'docket', comment: 'Which docket is this task associated with?'
        },

        roomNumber: {
            type: 'string', comment: 'The room number where the docket call is happening'
        },

        categories: {
            type: 'uuid[]', entityType: 'category', comment: 'To which categories is this task related?',
        },

        /**
         * You can wait on a person or another task.
         *
         * You can wait on more than one thing.
         *
         * Need to know if the wait is over:
         * - Waiting on a person (contactId): need to manually track when the task is finished
         * - Waiting on another task (taskId): wait is over when the task is marked completed
         *
         * _This should really be a ref field, not a json field within Task_
         */
        waitingOn: {
            type: 'jsonb[]',
            jsdoc: `{waitType: 'C'|'T', waitOn: uuid, waitUntil: number, description: string}`,
            comment: 'Who do I need input from to complete this task?',
        },

        completionDate: {
            type: 'timestamp', comment: 'Date and time when the task was completed',
        },

        /**
         * Are we assigning this to a user or a group?
         */
        assignedToType: {
            type: 'char',
            values: ['U', 'G'], // Task or Group
            valueNames: ['User', 'Group'],
            default: 'U',
            entity: {
                map: {U: 'user', G: 'group'}, // This is a mapping of key field to entityType
                value: 'assignedTo',
                preserveLockedAssignment: true,
                deleteStrategy: 'preserve' // If we delete a user or group, we still need the historical data
            },
            comment: 'Are we assigning this task to a user or a group?'
        },

        /**
         * The user or group this task is assigned to.
         */
        assignedTo: {
            type: 'uuid',
            comment: 'User or group this task is assigned to',
        },

        /**
         * People related to this task, such as people invited to the meeting. Usually outside of the team.
         */
        relatedTo: {
            type: 'uuid[]', entityType: 'contact', comment: 'People related to this task (outside of the team)',
        },

        /**
         * Arbitrary tags to assign to a task, used for searching for related tasks that may not be in the same
         * category.
         */
        tags: {type: 'string[]', comment: 'Any tags, for quick searches'},

        // Relationships to other stuff
        // Format: [ {module: 'Marketing', entity: 'Campaign', id: '00000-0000-000-00000'}, {...} ]
        // Allowed module/entity combinations are
        // Marketing/Campaign
        // Sales/Lead
        // Client/Matter
        tieIns: {
            type: 'json[]',
            required: false,
            jsdoc: '{module: string, entity: string, id: string}',
            entity: {
                key: 'entity',
                map: {Campaign: 'campaign', Lead: 'lead', Matter: 'matter'},
                value: 'id',
                deleteStrategy: 'delete' // removing a case/lead/campaign should remove the entry from the tie-in
            },
            comment: 'Tie-ins to other parts of the system, e.g. marketing campaigns'
        },

        // TODO: Need to abandon this in favor of laborRate... only used for Labor in Marketing Campaigns
        associatedRate: {
            type: 'money',
            default: 0.0,
            comment: 'The billingRate for this user at the time of creation/assignment'
        },

        // From activity
        activityTypeIds: {
            type: 'uuid[]', entityType: 'activityType', comment: 'The ids of the related activity types'
        },

        billable: {
            // Defaults true for Client work, false for all other, but can be overridden
            type: 'boolean',
            comment: 'Is this a billable activity? If so, all of the following fields will be displayed'
        },

        /* Billable fields */
        laborRate: {type: 'money', comment: 'This is based on the negotiated (labor) rate for this case'},
        laborRateOverride: {type: 'money', comment: 'A specific (labor) rate applied to this entry only'},
        isLaborRateOverridden: {type: 'boolean', comment: 'Is the (labor) rate being overridden?'},

        travelRate: {type: 'money', comment: 'This is based on either the negotiated travel rate, or the normal rate'},
        travelRateOverride: {type: 'money', comment: 'What rate are we billing for travel time?'},
        isTravelRateOverridden: {type: 'boolean', comment: 'Is the travel rate being overridden?'},

        laborTime: {type: 'float', default: 0, comment: 'How much actual time was spent on labor'},
        laborTimeOverride: {type: 'float', default: 0, comment: 'How much time are we billing for labor'},
        isLaborTimeOverridden: {type: 'boolean', comment: 'Is the labor time being overridden?'},

        travelTime: {type: 'float', default: 0, comment: 'How much actual time was spent on travel'},
        travelTimeOverride: {type: 'float', default: 0, comment: 'How much time are we billing for travel?'},
        isTravelTimeOverridden: {type: 'boolean', comment: 'Is the travel time being overridden?'},

        cost: {type: 'money', default: 0, comment: 'The total cost of this activity'},
        costOverride: {type: 'money', default: 0, comment: 'Manually overridden cost'},
        isCostOverridden: {
            type: 'boolean',
            comment: 'Is the cost being overridden, either manually, as a flat fee, or through a noCharge?'
        },

        noCharge: {type: 'boolean', comment: 'Do not charge anything for this entry'},

        hideFromInvoice: {type: 'boolean', comment: 'Should this entry be hidden from the invoice?'},

        // TODO: right now this is a 'fake' uuid, as it is stored within an array of objects within matter
        flatFeeBucket: {
            type: 'uuid',
            entityType: 'flatFee',
            // Either bucket alone keeps the entry at $0 (TaskHelper.getUpdatedCost), so releasing one releases both
            releaseWith: ['flatFeeMilestoneBucket'],
            preserveFinalizedTask: true, // Keep a Task's bucket references when it is on a finalized invoice, without saving the Task.
            comment: 'UUID of flat fee bucket'
        },
        flatFeeMilestoneBucket: {
            type: 'uuid',
            entityType: 'flatFeeMilestone',
            releaseWith: ['flatFeeBucket'],
            preserveFinalizedTask: true, // Keep a Task's bucket references when it is on a finalized invoice, without saving the Task.
            comment: 'Optional UUID of flat fee milestone bucket'
        },

        // Invoicing fields
        amountPaid: {type: 'money', default: 0.0, comment: 'Amount of the cost considered paid'},
        invoiceId: {type: 'uuid', entityType: 'invoice', comment: 'Invoice associated with this task'},
        invoiceNotes: {
            type: 'string',
            comment: 'Public notes for the invoice (if billable), or a description otherwise'
        },

        /**
         * Notification status is [r]eview, [w]aiting for approval, [c]ompleted. Null means not set.
         *
         * Here is the flow:
         *
         * Person A marks a task for review (r) by Person B, and leaves a comment on what to change.
         * - For Person A, they now see a flag with a red outline in TaskComponent and TaskItem.
         * - For Person B, they now see a flag filled red, with a red background in TaskComponent and a filled red
         *   flag in TaskItem.
         *
         * Person B updates the task and clicks on the thumbs up in TaskItem, indicating that they are done.
         * This sets the status to waiting for approval (w).
         * - Person A now sees a flag with an orange outline in TaskComponent and TaskItem.
         * - Person B now sees a filled orange flag in TaskComponent and TaskItem.
         *
         * Person A looks at the changes and hits thumbs up or thumbs down.
         * If thumbs up, the notification status is set to completed (c).
         * - Person A sees a flag with a green outline in TaskComponent and TaskItem.
         * - Person B sees a filled green flag in TaskComponent and TaskItem.
         *
         * If thumbs down, the notification is reset to review (r).
         */
        notification: {
            type: 'jsonb',
            jsdoc: `{status: 'r'|'c'|'w', from: uuid, to: uuid, message: string}`,
            // not adding an entity section because userIds can no longer be removed from the system (only disabled)
            default: '{}',
            comment: 'Notification from one user to another user that they should look at something'
        },

        /**
         * Used for automation/tracing.
         */
        fromTemplateId: {
            type: 'uuid',
            entityType: 'templateTask',
            comment: 'If this task was created from template, what template did it come from?'
        },
        emojis: {
            type: 'string[]',
            comment: 'emojis to display on the task'
        },

        /**
         * OWA-1314: set on a Task the Automation server created from a recurring series.
         */
        seriesId: {
            type: 'uuid',
            entityType: 'taskSeries',
            // The server keeps the stored link when a write leaves it null (migration 029), so
            // deleting a series leaves its occurrences as they are. A merge leaves them too:
            // `preserve` drops the merge cascade as well, and no TaskSeries merge is wired.
            deleteStrategy: 'preserve',
            keptWhenOmitted: true,
            comment: 'The recurring series this task is an occurrence of'
        },

        recurrenceId: {
            type: 'timestamp',
            keptWhenOmitted: true,
            comment: 'The original start of this occurrence under its series rule (RFC 5545 RECURRENCE-ID). It stays the same when this one occurrence is moved, so the series never creates it twice'
        },

        /* Transients */
        sources: {type: 'string[]', transient: true, comment: 'Used for drag and drop'},

        targets: {type: 'string[]', transient: true, comment: 'Used for drag and drop'},

        baseVersion: {
            type: 'json',
            transient: true,
            jsdoc: '{addedOn: number | null, dbAddedOn: number | null}',
            comment: 'OWA-998: the accepted version this edit began from ({addedOn: null, dbAddedOn: null} for a new task). The server refuses the save when it is no longer current, and never stores it.'
        },

        pendingOp: {
            type: 'string',
            transient: true,
            searchable: false,
            comment: 'OWA-998: browser only, never sent. The id of the not yet acknowledged save this copy\'s content came from; an edit of this copy continues that save.'
        },
    },

    // OWA-998: a save whose baseVersion is no longer current is refused (409 STALE), not written
    staleCheck: true,
    stopTimerOnVersionPromotion: true,


    /**
     * Indexes are simple filters, and do create segments. Simple true or false filters.
     */
    indexes: [
        {name: 'completed', fn: t => !!t.completionDate},
        {name: 'notCompleted', fn: t => !t.completionDate},

        {name: 'billable', fn: t => t.billable},
        {name: 'notBillable', fn: t => !t.billable},

        {name: 'billableAndCompleted', fn: t => t.billable && !!t.completionDate},

        {name: 'flagged', fn: t => t.notification && ['r', 'w'].includes(t.notification.status)},

        {name: 'private', fn: t => t.private},

        {name: 'fatalError', fn: t => t.fatalError && (t.fatalError.message || t.fatalError.status)},
    ],

    /**
     * Fields that should not cause a touch() on update
     */

    /**
     * Maps are like indexes, but have keys. This can be used to get all tasks for an invoiceId or matterId,
     * even if the key is buried in a structure.
     *
     * The name is the name of the mapping, which may or may not be the name of the field that holds the values.
     * The function takes two values, an object and a value. The function checks the object to see if the value
     * is present, and if so returns a true.
     */
    maps: [
        {
            name: 'userId',
            keysFn: t => t.assignedTo ?? t.createdBy,
            presentFn: (t, k) => (t.assignedTo ?? t.createdBy) === k
        },
        {
            name: 'invoiceId',
            keysFn: t => t.invoiceId,
            presentFn: (t, k) => t.invoiceId === k
        },
        // {
        // 	name: 'dueDate',
        // 	keysFn: t => t.dueDate ? format(t.dueDate, 'yyyy-MM-dd') : null,
        // 	presentFn: (t, k) => t.dueDate ? format(t.dueDate, 'yyyy-MM-dd') === k : null === k
        // },
        // {
        // 	name: 'startTime',
        // 	keysFn: t => t.startTime ? format(t.startTime, 'yyyy-MM-dd') : null,
        // 	presentFn: (t, k) => t.startTime ? format(t.startTime, 'yyyy-MM-dd') === k : null === k
        // },
        {
            name: 'calendarDate', // We want either the start time, if it exists, or the due date
            keysFn: t => {
                const dt = t.startTime || t.dueDate;
                // @ts-ignore
                return dt ? format(dt, 'yyyy-MM-dd') : null;
            },
            presentFn: (t, k) => {
                const dt = t.startTime || t.dueDate;
                // @ts-ignore
                return dt ? format(dt, 'yyyy-MM-dd') === k : null === k;
            }
        },
        {
            name: 'flatFeeBucket',
            keysFn: t => t.flatFeeBucket,
            presentFn: (t, k) => t.flatFeeBucket === k
        },
        {
            name: 'flatFeeMilestoneBucket',
            keysFn: t => t.flatFeeMilestoneBucket,
            presentFn: (t, k) => t.flatFeeMilestoneBucket === k
        },
        {
            name: 'matterId',
            // @ts-ignore
            keysFn: t => t.tieIns.filter(ti => ti.module === 'Client' && ti.entity === 'Matter').map(ti => ti.id),
            // @ts-ignore
            presentFn: (t, k) => t.tieIns.some(ti => ti.id === k)
        },
        {
            name: 'leadId',
            // @ts-ignore
            keysFn: t => t.tieIns.filter(ti => ti.module === 'Sales' && ti.entity === 'Lead').map(ti => ti.id),
            // @ts-ignore
            presentFn: (t, k) => t.tieIns.some(ti => ti.id === k)
        },
        {
            name: 'campaignId',
            // @ts-ignore
            keysFn: t => t.tieIns.filter(ti => ti.module === 'Marketing' && ti.entity === 'Campaign').map(ti => ti.id),
            // @ts-ignore
            presentFn: (t, k) => t.tieIns.some(ti => ti.id === k)
        },
        {
            name: 'activityTypeId',
            keysFn: t => t.activityTypeIds,
            presentFn: (t, k) => t.activityTypeIds && t.activityTypeIds.includes(k)
        }, {
            name: 'contactId',
            keysFn: t => t.relatedTo,
            presentFn: (t, k) => t.relatedTo && t.relatedTo.includes(k)
        },
        {
            name: 'docketId',
            keysFn: t => t.docketId,
            presentFn: (t, k) => t.docketId === k
        },
        {
            name: 'categories',
            keysFn: t => t.categories,
            presentFn: (t, k) => t.categories?.includes(k)
        },
        {
            name: 'fromTemplateId',
            keysFn: t => t.fromTemplateId,
            presentFn: (t, k) => t.fromTemplateId === k
        },
        {
            // OWA-1457: a series' delete impact finds its occurrences through this map
            name: 'seriesId',
            keysFn: t => t.seriesId,
            presentFn: (t, k) => t.seriesId === k
        },
    ],
    displayFn: 'this.title',
    hooks: [
        {when: 'after', appliesTo: 'find', method: 'updateMilestoneBucket', async: false},
        {when: 'before', appliesTo: 'save', method: 'processTask', async: false}
    ],
    sortOrder: [
        {position: 1, fieldName: 'dueDate', asc: true},
        {position: 2, fieldName: 'urgency', asc: false},
        {position: 3, fieldName: 'impact', asc: false},
        {position: 4, fieldName: 'assignedTo', asc: false}
    ]
});
    }
}
export default Task;
